import {cdnImage} from '@/util/cdnImage';
import AggregateScript from '@/components/Common/Aggregate-script';
import {MUIButton, MUIContainer, MUIGrid, MUITypography} from '@/components/MUI-Components';
import {CENTRALIZED_CRYPTOWALLET_META_DATA} from '@/util/metaData/crypto';
import Box from '@mui/material/Box';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import {CDN_URL, SITE_URL} from '@/constants/urls';
import {borderRadius, colors, fontSize} from '@/styles/tokens';

// `&&`/`&&&` repeat the emotion-generated class to raise specificity above the sitewide
// `.seamless-efficient-wrapper` and `.spectrum-header-wrapper .spectrum-heading span`
// fallback rules in globals.scss, which otherwise silently win (equal/higher specificity,
// same !important tier) over a plain single-class sx override.
const seamlessEfficientWrapperSx = {
    '&&': {backgroundColor: `${colors.primary.ctaDeep} !important`},
    '&&& .spectrum-heading span': {color: `${colors.accent.goldWarm} !important`}
} as const;
const readyRebarkWrapperSx = {backgroundColor: colors.accent.plumDark} as const;

const getStartedButtonSx = {
    background: `linear-gradient(90deg, ${colors.accent.orange} 0%, ${colors.accent.orangeDark} 100%)`,
    fontFamily: "'Headers', sans-serif",
    textTransform: 'none',
    padding: '10px 30px',
    border: `1px solid ${colors.accent.orange}`,
    fontSize: fontSize[16],
    boxShadow: 'none',
    borderRadius: borderRadius.lg,
    '&:hover': {
        background: 'transparent',
        border: `1px solid ${colors.accent.orange}`,
        color: colors.accent.orange
    }
} as const;

// Preserved from the page's former centralizedCryptoWallet.module.scss —
// scoped to this page's own Banner call, no component change needed.
const centralizedBannerImageBoxSx = {
    textAlign: 'center !important',
    '& img': {
        width: '450px !important',
        '@media (max-width: 1199px)': {width: '300px !important'},
        '@media (max-width: 394px) and (min-width: 362px)': {width: '100% !important'}
    }
} as const;
const advimg = cdnImage('centralized-crypto-wallet/centralized-short-banner.webp');
const cryptoshort = cdnImage('centralized-crypto-wallet/crypto-landscape-shotbnr.webp');
const Banner = dynamic(() => import('@/components/Ui-components/Banner'));
const HelperSection = dynamic(() => import('@/components/Ui-components/helper-section'));
const TransportService = dynamic(() => import('@/components/Ui-components/TransportService'));
const OneStepSolution = dynamic(() => import('@/components/Ui-components/one-step-solution'));
const Questions = dynamic(import('@/components/Ui-components/questions'));
const Testimonials = dynamic(() => import('@/components/Ui-components/Testimonials'));
const LatestInsights = dynamic(() => import('@/components/Ui-components/latest-insights'));
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));
const LatestProcess = dynamic(() => import('@/components/Ui-components/latestProcess'));

const centralizedCrypto = (props: any) => {
    const {
        openForm,
        OneStepDataCryptoCentralized,
        centralizedCryptoFAQData,
        centralizedCryptoServiceData,
        centralizedCryptoSolution,
        centralizedProcessData
    } = props;
    return (
        <Box className="centralized-cryptwallet-main-parent">
            <PageHead meta={CENTRALIZED_CRYPTOWALLET_META_DATA} url="/centralized-crypto-wallet-development" />

            <Banner
                priority={true}
                handleClickOpen={openForm}
                heading="Centralized Crypto Wallet Development Services"
                subheading="Optimize your digital asset management with our Centralized Crypto Wallet Development. Experience secure, convenient control over cryptocurrencies through user-friendly wallets on top exchanges. Enhance your crypto journey with the best in centralized vs decentralized crypto solutions."
                button="Elevate your journey with us."
                image={cdnImage('centralized-crypto-wallet/Centralized Crypto Wallet Development Services.webp')}
                bannerClass="centralized-banner"
                bannerImageBoxSx={centralizedBannerImageBoxSx}
                // bannerBackground="centralized-crypto-banner-background"
                alt="Centralized Crypto Wallet Development Services"
            />
            <MUIGrid item xs={12} className="helper-section">
                <Box className="helper_section_parent">
                    <MUIContainer>
                        <Box className="spectrum-header-wrapper solutions top-space">
                            <MUITypography className="spectrum-heading color-black " component="h2">
                                Explore The Robust Features In Our Centralized Crypto Wallet Solutions
                            </MUITypography>
                            <Box className="divder-wrapper">
                                <hr className="divider-line" />
                            </Box>
                            <Box className="sub-heading-box" pb={3.75}>
                                <MUITypography className="spectrum-sub-heading color-black spacing">
                                    Our feature-rich, centralized exchange crypto wallets excel in providing unmatched security,
                                    convenience, and control for your digital assets management. Designed for seamless navigation among
                                    cryptocurrency holdings, these wallets stand out with cutting-edge technology, ideal for users comparing
                                    the best centralized vs decentralized crypto solutions within the extensive list of centralized crypto
                                    exchanges.
                                </MUITypography>
                            </Box>
                        </Box>

                        <MUIGrid container spacing={2}>
                            <HelperSection data={centralizedCryptoSolution} className="home-helper-card" />
                        </MUIGrid>
                        <MUITypography className="one-step-footer">
                            Explore Seamless Asset Management With Our Leading Centralized Crypto Wallet, The Best In Crypto Exchanges
                        </MUITypography>

                        <MUIGrid container justifyContent="center">
                            <Box className="bottom-space">
                                <MUIButton
                                    label="Get Started"
                                    variant="contained"
                                    aria-label="Book Consultation"
                                    onClick={openForm}
                                    sx={getStartedButtonSx}
                                />
                            </Box>
                        </MUIGrid>
                    </MUIContainer>
                </Box>
            </MUIGrid>

            <Box>
                <MUIContainer>
                    <LatestProcess
                        heading="Comprehensive Process for Developing Centralized Crypto Wallets"
                        subheading="Our systematic, step-by-step process for developing centralized crypto wallets ensures surpassing expectations and establishing new benchmarks within the centralized vs decentralized crypto exchanges landscape, including a focus on the best practices for integrating with top centralized crypto exchanges and offering insights on navigating decentralized vs centralized crypto dynamics."
                        processData={centralizedProcessData}
                    />
                </MUIContainer>
            </Box>
            <Box className="one-step top-space bottom-spacing">
                <MUIContainer>
                    <OneStepSolution
                        OneStepData={OneStepDataCryptoCentralized}
                        sectionhead="Enhanced Security-Centric Features in Debut Infotech’s Centralized Crypto Wallet"
                        subhead="Debut Infotech excels in embedding advanced, security-centric features into our premier centralized crypto wallet, designed for utmost asset protection. Emphasizing a peace-of-mind experience, our wallet bridges the gap between centralized vs decentralized crypto functionalities, offering a robust solution on par with the best centralized crypto exchanges. Our platform is a standout in the list of centralized crypto exchanges, ensuring the safety of your assets with top-tier security measures."
                        className="centralized-crypto-solution"
                    />
                </MUIContainer>
            </Box>
            <Box className="hire-native-team seamless-efficient-wrapper" sx={seamlessEfficientWrapperSx}>
                <MUIContainer>
                    <MUIGrid item xs={12}>
                        <MUIGrid container spacing={3} justifyContent="space-around" pb={3.75}>
                            <MUIGrid item xs={12} sm={12} md={6} lg={7}>
                                <Box className="spectrum-header-wrapper solution-header-wrapper solutions">
                                    <MUITypography className="spectrum-heading top-spacing">
                                        Secure Your Assets With Unmatched Protection By Acting Fast And Choosing{' '}
                                        <span> Our Top-Tier Centralized Crypto Wallet</span> For Peace Of Mind.
                                    </MUITypography>
                                    <Box className="top-spacing ">
                                        <MUIButton className="nft-button" variant="contained" label="Get Started" onClick={openForm} />
                                    </Box>
                                </Box>
                            </MUIGrid>
                            <MUIGrid item xs={12} sm={12} md={6} lg={5} className="native-img top-spacing" textAlign="center">
                                <DisplayImage
                                    src={advimg}
                                    alt="Blockchain devlopment company "
                                    className="exchange-adv-img"
                                    width={450}
                                    height={400}
                                />
                            </MUIGrid>
                        </MUIGrid>
                    </MUIGrid>
                </MUIContainer>
            </Box>
            <Box className="top-space">
                <MUIContainer>
                    <TransportService
                        heading="Why Choose Debut Infotech for Your Centralized Crypto Wallet Development Services?"
                        subheading="Debut Infotech emerges as a leading pioneer in centralized crypto wallet solutions, offering unmatched expertise and innovative strategies. With a notable history of excellence in both centralized and decentralized crypto exchanges, Debut Infotech provides key advantages for those seeking top-tier centralized exchange crypto development services, ensuring a path towards success:"
                        service={centralizedCryptoServiceData}
                        className="cenetralized-crypto-transport-card"
                    />
                </MUIContainer>
            </Box>
            <Box className="hire-native-team seamless-efficient-wrapper ready-rebark-wrapper margin" sx={readyRebarkWrapperSx}>
                <MUIContainer>
                    <MUIGrid item xs={12}>
                        <MUIGrid container spacing={3} justifyContent="space-around">
                            <MUIGrid item xs={12} sm={12} md={6} lg={7}>
                                <Box className="spectrum-header-wrapper solution-header-wrapper solutions">
                                    <MUITypography className="spectrum-heading top-spacing">
                                        Ready to Elevate Your Business with Centralized Crypto?
                                    </MUITypography>
                                    <Box pt={2}>
                                        <MUITypography className="spectrum-sub-heading color-white" component="body1">
                                            Explore the benefits of centralized crypto exchanges for superior security, transaction speed,
                                            and user experience. Partner with us to elevate your business.
                                        </MUITypography>
                                    </Box>
                                    <Box className="top-spacing ">
                                        <MUIButton className="nft-button" variant="contained" label="Get Started" onClick={openForm} />
                                    </Box>
                                </Box>
                            </MUIGrid>
                            <MUIGrid item xs={12} sm={12} md={6} lg={5} className="native-img top-spacing" textAlign="center">
                                <DisplayImage
                                    src={cryptoshort}
                                    alt="Blockchain devlopment company "
                                    className="exchange-adv-img"
                                    width={300}
                                    height={280}
                                />
                            </MUIGrid>
                        </MUIGrid>
                    </MUIGrid>
                </MUIContainer>
            </Box>
            <MUIGrid item xs={12} className="question-parent top-space mobile-top-padding">
                <MUIContainer>
                    <Questions accordianData={centralizedCryptoFAQData} />
                </MUIContainer>
            </MUIGrid>

            <MUIGrid item xs={12}>
                <Box className="top-space mobile-top-padding bg-blue-background">
                    <Testimonials heading="Our Clients Describe Us Best" />
                </Box>
            </MUIGrid>

            <MUIGrid item xs={12} className="latest-insights top-space mobile-top-padding">
                <LatestInsights id="22" />
            </MUIGrid>
            <AggregateScript
                name="Centralized Crypto Wallet Development"
                image={`${CDN_URL}?url=%2Fimages%2Fcentralized-crypto-wallet%2FCentralized%20Crypto%20Wallet%20Development%20Services.webp&w=750&q=75`}
                description="Develop secure and high-performance centralized crypto wallets with multi-layer security, real-time transaction tracking, and seamless asset management. Designed for exchanges and enterprises prioritizing speed, control, and user experience."
                offers={{
                    '@type': 'AggregateOffer',
                    url: `${SITE_URL}/centralized-crypto-wallet-development`,
                    priceCurrency: 'USD',
                    lowPrice: '2000',
                    highPrice: '5000',
                    offerCount: '10'
                }}
                aggregateRating={{
                    '@type': 'AggregateRating',
                    ratingValue: '4.8',
                    bestRating: '5',
                    worstRating: '2',
                    ratingCount: '499'
                }}
            />
        </Box>
    );
};
export default centralizedCrypto;
centralizedCrypto.Layout = 'Main';

export const getStaticProps = async () => {
    const {
        OneStepDataCryptoCentralized,
        centralizedCryptoFAQData,
        centralizedCryptoServiceData,
        centralizedCryptoSolution,
        centralizedProcessData
    } = await import('@/util/contents/centralized-cryptowallet-data');
    return {
        props: {
            OneStepDataCryptoCentralized,
            centralizedCryptoFAQData,
            centralizedCryptoServiceData,
            centralizedCryptoSolution,
            centralizedProcessData
        }
    };
};
